import { useState } from 'react';

import { Tooltip } from '@/components/Tooltip/Tooltip';

import type { AddVideoResult } from './useContentEditor';
import styles from './VideoSectionEditor.module.css';

interface VideoSectionEditorProps {
  disabled?: boolean;
  onAddVideo: (value: string) => AddVideoResult;
  onRemoveVideo: (index: number) => void;
  onReorderVideo: (fromIndex: number, toIndex: number) => void;
  variant?: 'card' | 'inline';
  videoIds: string[];
}

function UpIcon() {
  return (
    <svg aria-hidden="true" height="16" viewBox="0 0 24 24" width="16">
      <path
        d="m6 14 6-6 6 6"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  );
}

function DownIcon() {
  return (
    <svg aria-hidden="true" height="16" viewBox="0 0 24 24" width="16">
      <path
        d="m6 10 6 6 6-6"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  );
}

function RemoveIcon() {
  return (
    <svg aria-hidden="true" height="16" viewBox="0 0 24 24" width="16">
      <path
        d="m6 6 12 12M18 6 6 18"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  );
}

function getThumbnailUrl(videoId: string) {
  return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
}

export function VideoSectionEditor({
  disabled = false,
  onAddVideo,
  onRemoveVideo,
  onReorderVideo,
  variant = 'card',
  videoIds,
}: VideoSectionEditorProps) {
  const [inputValue, setInputValue] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleAddVideo = () => {
    const result = onAddVideo(inputValue);

    if (!result.success) {
      setErrorMessage(result.error ?? 'Unable to add this video right now.');
      return;
    }

    setInputValue('');
    setErrorMessage(null);
  };

  return (
    <section
      aria-labelledby="video-section-editor-heading"
      className={styles.root}
      data-variant={variant}
    >
      <div className={styles.header}>
        <div>
          <p className={styles.eyebrow}>Video Section</p>
          <h2 className={styles.title} id="video-section-editor-heading">
            Videos
          </h2>
        </div>
        <p className={styles.summary}>
          Control the YouTube videos that appear at the top of this lesson.
        </p>
      </div>

      <div className={styles.addRow}>
        <label className={styles.field} htmlFor="video-section-editor-input">
          <span className={styles.label}>YouTube Video ID</span>
          <input
            className={styles.input}
            disabled={disabled}
            id="video-section-editor-input"
            onChange={(event) => {
              setInputValue(event.target.value);
              if (errorMessage) {
                setErrorMessage(null);
              }
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleAddVideo();
              }
            }}
            placeholder="Enter YouTube Video ID"
            type="text"
            value={inputValue}
          />
        </label>

        <button
          className={styles.addButton}
          disabled={disabled}
          onClick={handleAddVideo}
          type="button"
        >
          Add Video
        </button>
      </div>

      {errorMessage ? (
        <p className={styles.errorMessage} role="alert">
          {errorMessage}
        </p>
      ) : null}

      {videoIds.length === 0 ? (
        <p className={styles.emptyState}>
          No videos added. Add a YouTube video ID to show videos at the top of this
          lesson.
        </p>
      ) : (
        <ul className={styles.list}>
          {videoIds.map((videoId, index) => (
            <li className={styles.listItem} key={`${videoId}-${index}`}>
              <img
                alt={`Thumbnail for ${videoId}`}
                className={styles.thumbnail}
                src={getThumbnailUrl(videoId)}
              />

              <div className={styles.videoMeta}>
                <p className={styles.videoId}>{videoId}</p>
                <p className={styles.videoPosition}>Video {index + 1}</p>
              </div>

              <div className={styles.actions}>
                <Tooltip content="Move video up">
                  <button
                    aria-label="Move video up"
                    className={styles.iconButton}
                    disabled={disabled || index === 0}
                    onClick={() => {
                      onReorderVideo(index, index - 1);
                    }}
                    type="button"
                  >
                    <UpIcon />
                  </button>
                </Tooltip>
                <Tooltip content="Move video down">
                  <button
                    aria-label="Move video down"
                    className={styles.iconButton}
                    disabled={disabled || index === videoIds.length - 1}
                    onClick={() => {
                      onReorderVideo(index, index + 1);
                    }}
                    type="button"
                  >
                    <DownIcon />
                  </button>
                </Tooltip>
                <Tooltip content={`Remove video ${videoId}`}>
                  <button
                    aria-label={`Remove video ${videoId}`}
                    className={styles.removeButton}
                    disabled={disabled}
                    onClick={() => {
                      onRemoveVideo(index);
                    }}
                    type="button"
                  >
                    <RemoveIcon />
                  </button>
                </Tooltip>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
